import { Component, OnInit } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { IntranetoldService } from 'src/app/api/intranetold.service';
import { variableGlobal } from 'src/app/global.enum';
import Swal from 'sweetalert2';

interface MenuItem {
  _id?: string;
  MENUs: string;
  PADRE: string;
  ORDEN: number;
  TITULO: string;
  ICONO: string;
  URL: string;
  NIVEL: number;
  ACTIVO: boolean;
  CONTENIDO?: any;
  SUB_TITULO?: any;
  FECHA_CREACION?: any;
  FECHA_ACTUALIZACION?: any;
  // UI helpers
  children?: MenuItem[];
  expanded?: boolean;
}

@Component({
  selector: 'app-menu-admin',
  templateUrl: './menu-admin.component.html',
  styleUrls: ['./menu-admin.component.sass']
})
export class MenuAdminComponent implements OnInit {

  cargando = true;
  allMenus: MenuItem[] = [];
  tree: MenuItem[] = [];

  // Form model
  editItem: MenuItem = this.nuevoMenu();
  isEditing = false;
  editTitle = 'Crear Menú';

  // Parents list for dropdown
  parentOptions: MenuItem[] = [];

  constructor(
    private _api: IntranetoldService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.cargarMenus();
  }

  // ─── Data loading ───────────────────────────────────────────────

  async cargarMenus() {
    this.cargando = true;
    try {
      const res: any = await this._api.getMenuAll();
      if (res?.estado && Array.isArray(res.objeto)) {
        this.allMenus = res.objeto;
      } else {
        this.allMenus = [];
      }
    } catch (e) {
      
      this.allMenus = [];
    }
    this.buildTree();
    this.cargando = false;
  }

  buildTree() {
    const sorted = [...this.allMenus].sort((a, b) => (a.ORDEN || 0) - (b.ORDEN || 0));
    const parents = sorted.filter(m => !m.PADRE || m.PADRE === '');
    parents.forEach(p => {
      p.children = sorted.filter(c => c.PADRE === p._id);
      if (p.expanded === undefined) p.expanded = true;
    });
    this.tree = parents;
    this.parentOptions = parents;
  }

  // ─── CRUD ───────────────────────────────────────────────────────

  nuevoMenu(): MenuItem {
    return {
      MENUs: '',
      PADRE: '',
      ORDEN: 0,
      TITULO: '',
      ICONO: 'fas fa-circle',
      URL: '',
      NIVEL: 0,
      ACTIVO: true
    };
  }

  abrirCrear(modalRef: any, padreId: string = '') {
    this.editItem = this.nuevoMenu();
    this.editItem.PADRE = padreId;
    if (padreId) {
      this.editItem.NIVEL = 1;
      const parent = this.allMenus.find(m => m._id === padreId);
      const siblings = this.allMenus.filter(m => m.PADRE === padreId);
      this.editItem.ORDEN = siblings.length > 0
        ? Math.max(...siblings.map(s => s.ORDEN || 0)) + 1
        : 0;
    } else {
      this.editItem.NIVEL = 0;
      this.editItem.ORDEN = this.tree.length > 0
        ? Math.max(...this.tree.map(t => t.ORDEN || 0)) + 1
        : 0;
    }
    this.isEditing = false;
    this.editTitle = padreId ? 'Crear Submenú' : 'Crear Menú';
    this.modalService.open(modalRef, this.modalOpts());
  }

  abrirEditar(modalRef: any, item: MenuItem) {
    this.editItem = { ...item };
    this.isEditing = true;
    this.editTitle = 'Editar Menú';
    this.modalService.open(modalRef, this.modalOpts());
  }

  async guardar(modal: any) {
    if (!this.editItem.MENUs || !this.editItem.MENUs.trim()) {
      Swal.fire('Error', 'El nombre del menú es obligatorio.', 'error');
      return;
    }
    try {
      Swal.fire({ title: 'Guardando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const res: any = await this._api.AddEditMenu(this.editItem);
      Swal.close();
      if (res?.estado) {
        Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 2000 })
          .fire({ icon: 'success', title: res.mensaje || 'Guardado' });
        modal.close();
        await this.cargarMenus();
      } else {
        Swal.fire('Error', res?.mensaje || 'No se pudo guardar', 'error');
      }
    } catch (e) {
      Swal.close();
      Swal.fire('Error', 'Error de conexión al guardar', 'error');
    }
  }

  async eliminar(item: MenuItem) {
    const result = await Swal.fire({
      title: '¿Eliminar este menú?',
      text: `Se eliminará "${item.MENUs || item.TITULO}"`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });
    if (!result.isConfirmed) return;

    try {
      Swal.fire({ title: 'Eliminando...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const res: any = await this._api.EliminaMenu(item);
      Swal.close();
      if (res?.estado) {
        Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 2000 })
          .fire({ icon: 'success', title: res.mensaje || 'Eliminado' });
        await this.cargarMenus();
      } else {
        Swal.fire('Error', res?.mensaje || 'No se pudo eliminar', 'error');
      }
    } catch (e) {
      Swal.close();
      Swal.fire('Error', 'Error de conexión', 'error');
    }
  }

  // ─── Drag & Drop ───────────────────────────────────────────────

  async dropParent(event: CdkDragDrop<MenuItem[]>) {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.tree, event.previousIndex, event.currentIndex);
    await this.persistOrden(this.tree);
  }

  async dropChild(event: CdkDragDrop<MenuItem[]>, parent: MenuItem) {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(parent.children, event.previousIndex, event.currentIndex);
    await this.persistOrden(parent.children);
  }

  private async persistOrden(items: MenuItem[]) {
    try {
      for (let i = 0; i < items.length; i++) {
        if (items[i].ORDEN !== i) {
          items[i].ORDEN = i;
          await this._api.AddEditMenu(items[i]);
        }
      }
      Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 })
        .fire({ icon: 'success', title: 'Orden actualizado' });
    } catch (e) {
      Swal.fire('Error', 'No se pudo guardar el nuevo orden', 'error');
      await this.cargarMenus();
    }
  }

  // ─── Helpers ────────────────────────────────────────────────────

  toggleExpand(parent: MenuItem) {
    parent.expanded = !parent.expanded;
  }

  private modalOpts(): NgbModalOptions {
    return {
      backdrop: 'static',
      keyboard: false,
      size: 'lg',
      modalDialogClass: variableGlobal.CLASS_MODAL_BG
    };
  }
}
